<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { useJoakimStore } from '@/stores/useJoakimStore'
import { LoanInformation } from '@/types/JoakimTypes/LoanInformation'
import DatePicker from '../../Shared/DatePicker.vue'
import { add } from 'date-fns'

const props = defineProps<{
  disable?: boolean
}>()

interface SkuldabrefResponse {
  skuldabref?: Array<{ innheimtunumer?: string | number; nafnverd?: string | number }>;
}

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()
const joakimStore = useJoakimStore()

function addChild() {
  umsokn.value.loanInformation.push({
    loanNumber: '',
    initialAmount: '',
    issueDate: ''
  })
}

function removeChild(index: number) {
  umsokn.value.loanInformation.splice(index, 1)
  if (umsokn.value.loanInformation.length === 0) {
    addChild()
  }
}



onLoaded(async () => {
  if (!umsokn.value) return

  // list initializations
  if (!umsokn.value.loanInformation) umsokn.value.loanInformation = []
  if (!umsokn.value.coDeptors) umsokn.value.coDeptors = []
  if (!umsokn.value.loanBefore) umsokn.value.loanBefore = []
  if (!umsokn.value.currentDeptorInformation) umsokn.value.currentDeptorInformation = []
  if (!umsokn.value.loantakers) umsokn.value.loantakers = []

  // objects that might come back as null but should exist for binding
  if (!umsokn.value.takeOverLoanInformation) {
    umsokn.value.takeOverLoanInformation = {
      loanNumber: '',
      initialAmount: '',
      mortgage: null,
      issueDate: ''
    }
  }

  await joakimStore.getSkuldabref();

  const resp = joakimStore.skuldabref as unknown as SkuldabrefResponse | null;
  const skuldabrefData = resp?.skuldabref ?? [];

  if (
    skuldabrefData.length > 0 &&
    (!umsokn.value.loanInformation || umsokn.value.loanInformation.length === 0)
  ) {
    umsokn.value.loanInformation = skuldabrefData.map((s: any) => ({
      loanNumber: String(s.innheimtunumer ?? ''),
      initialAmount: String(s.nafnverd ?? ''),
      issueDate: '' // no direct mapping in old data
    }))
  } else if (!umsokn.value.loanInformation || umsokn.value.loanInformation.length === 0) {
    umsokn.value.loanInformation = [
      { loanNumber: '', initialAmount: '', issueDate: '' }
    ]
  }

  
})

// watch: auto-format initialAmount with thousand separators
watch(
  () => umsokn.value.loanInformation,
  (list: LoanInformation[]) => {
    list.forEach((el: LoanInformation) => {
      if (el?.initialAmount) {
        el.initialAmount = el.initialAmount
          .replace(/\D/g, '')
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
    })
  },
  { deep: true }
)
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_vedflutning_step2_header') }}
      </div>

      <transition-group name="list">
        <div
          v-for="(item, index) in umsokn.loanInformation"
          :key="'loanInformation_' + index"
          class="list-item"
        >
          <div v-if="index > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              type="button"
              class="forms__warning__list__wrapper"
              @click="removeChild(index)"
            >
              <img
                class="forms__warning__icon"
                src="@/assets/icons/Close.svg"
                alt="Remove"
              />
            </button>
          </div>

          <v-row class="animated__label">
            <v-col md="4" cols="12">
              <v-text-field
                v-model="item.loanNumber"
                :readonly="props.disable"
                 density="comfortable"
                :label="t('lan_vedflutning_step2_lansnumer')"
                variant="outlined"
                
              />
            </v-col>
            <v-col md="4" cols="12">
              <v-text-field
                v-model="item.initialAmount"
                :readonly="props.disable"
                 density="comfortable"
                :label="t('lan_vedflutning_step2_fjarhaed')"
                variant="outlined"
              />
            </v-col>
            <v-col md="4" cols="12">
              <DatePicker
                v-model="item.issueDate"
                :label="t('lan_vedflutning_step2_utgafudagur')"
                :disabled="props.disable"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addChild"
        >
          {{ $t('lan_vedflutning_step2_baetavid') }}
        </v-btn>
      </div>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.forms__divider__container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
  position: relative;
}
.forms__divider {
  flex: 1;
  height: 1px;
  background-color: #ddd;
  margin-right: 8px;
}
.forms__warning__list__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
}
.forms__warning__icon {
  width: 16px;
  height: 16px;
  pointer-events: none;
}
</style>
